import * as z from "zod/v4";
import { alias } from "../util/alias.ts";
import { css } from "lit";

const CATEGORY_ALIASES: Record<string, string> = {
  // Canonical (identity, for case-insensitive matching)
  social: "social",
  technological: "technological",
  economic: "economic",
  environmental: "environmental",
  political: "political",
  // Old canonical values (backwards compat)
  tech: "technological",
  economics: "economic",
  environment: "environmental",
  politics: "political",
  // Other plausible agent variants
  technology: "technological",
};

export const ForceCategorySchema = z
  .enum(["social", "technological", "economic", "environmental", "political"])
  .describe("The STEEP category of the driving force");

export const CoerceForceCategorySchema = z.preprocess(
  alias(CATEGORY_ALIASES),
  ForceCategorySchema,
);

export type ForceCategory = z.infer<typeof ForceCategorySchema>;

export const into = (value: unknown): ForceCategory | undefined => {
  const { data, success } = CoerceForceCategorySchema.safeParse(value);
  if (!success) return undefined;
  return data;
};

export const toDesc = (category: ForceCategory | undefined) => {
  switch (category) {
    case "social":
      return "Social";
    case "technological":
      return "Technological";
    case "economic":
      return "Economic";
    case "environmental":
      return "Environmental";
    case "political":
      return "Political";
    default:
      return "None";
  }
};

/** Include in component styles */
export const forceCategoryStyles = css`
  :host {
    /* Domain-specific category colors (OKLCH) */
    --category-pink: oklch(0.65 0.18 330);
    --category-pink-bg: color-mix(
      in oklch,
      var(--category-pink),
      transparent 70%
    );
    --category-purple: oklch(0.55 0.2 290);
    --category-purple-bg: color-mix(
      in oklch,
      var(--category-purple),
      transparent 70%
    );
    --category-teal: oklch(0.6 0.12 200);
    --category-teal-bg: color-mix(
      in oklch,
      var(--category-teal),
      transparent 70%
    );
    --category-green: oklch(0.55 0.15 145);
    --category-green-bg: color-mix(
      in oklch,
      var(--category-green),
      transparent 70%
    );
    --category-red: oklch(0.55 0.2 25);
    --category-red-bg: color-mix(
      in oklch,
      var(--category-red),
      transparent 70%
    );

    /* Semantic category tokens */
    --category-social: var(--category-pink);
    --category-social-bg: var(--category-pink-bg);
    --category-technological: var(--category-purple);
    --category-technological-bg: var(--category-purple-bg);
    --category-economic: var(--category-teal);
    --category-economic-bg: var(--category-teal-bg);
    --category-environmental: var(--category-green);
    --category-environmental-bg: var(--category-green-bg);
    --category-political: var(--category-red);
    --category-political-bg: var(--category-red-bg);
    --category-none: var(--muted-foreground);
    --category-none-bg: var(--muted);
  }
`;

export const toColor = (category: ForceCategory | undefined) => {
  switch (category) {
    case "social":
      return `var(--category-social)`;
    case "technological":
      return `var(--category-technological)`;
    case "economic":
      return `var(--category-economic)`;
    case "environmental":
      return `var(--category-environmental)`;
    case "political":
      return `var(--category-political)`;
    default:
      return "var(--category-none)";
  }
};

export const toColorScrim = (category: ForceCategory | undefined) => {
  switch (category) {
    case "social":
      return `var(--category-social-bg)`;
    case "technological":
      return `var(--category-technological-bg)`;
    case "economic":
      return `var(--category-economic-bg)`;
    case "environmental":
      return `var(--category-environmental-bg)`;
    case "political":
      return `var(--category-political-bg)`;
    default:
      return "var(--category-none-bg)";
  }
};
